import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { useDeviceLayout } from '@vio/atlas/context/deviceLayout';
import { Divider } from '@vio/atlas/Divider';
import { Flex } from '@vio/atlas/helpers/Flex';
import { SkeletonLoader } from '@vio/atlas/SkeletonLoader';
import { Text } from '@vio/atlas/Text';
import { getAllFilterKeys } from 'components/Filters/utils';
import { HotelCardContext } from 'components/HotelCardContext';
import { useIsExperimentalNoOffersCardEnabled } from 'components/HotelCardMobile/ExperimentalNoOffersCard/helpers';
import { useFiltersForm } from 'hooks/useFiltersForm';
import { getCurrencyCode } from 'modules/meta/selectors';
import { getHotel } from 'modules/sapiSearch/selectors';
import { getListOfValues } from 'modules/search/selectors';
import { prop } from 'ramda';
import { getEnabledCurrencies } from '@daedalus/core/src/localization/business/currency';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { getMatchingFilters } from './getMatchingFilters';
const _exp = /*#__PURE__*/() => Flex.Row;
const ScrollWrapper = /*#__PURE__*/styled(_exp())({
  name: "ScrollWrapper",
  class: "s103h3df",
  propsAsIs: true
});
const ScrollWrapperB = /*#__PURE__*/styled('div')({
  name: "ScrollWrapperB",
  class: "s949btl",
  propsAsIs: false
});
const _exp7 = /*#__PURE__*/() => Divider;
const PartialFiltersDivider = /*#__PURE__*/styled(_exp7())({
  name: "PartialFiltersDivider",
  class: "p1y44rvp",
  propsAsIs: true
});
const paddingStyle = "pnz7w8w";
const strikeThrough = "s32l33v";
const MiddleDot = () => <span>·</span>;
const allFilterKeys = getAllFilterKeys();
interface Props {
  hasBasicPropertyDetails: boolean;
}
const PartialFiltersMatchComponent: React.FC<Props> = ({
  hasBasicPropertyDetails
}) => {
  const {
    getFilters,
    getAppliedStarsLabel
  } = useFiltersForm(allFilterKeys);
  const listOfValues = useSelector(getListOfValues);
  const {
    formatMessage
  } = useIntl();
  const {
    hotelId
  } = useContext(HotelCardContext);
  const {
    isMobile
  } = useDeviceLayout();
  const currencyCode = useSelector(getCurrencyCode);
  const hotel = useSelector(state => getHotel(state, hotelId));
  const filters = getFilters();
  const isExperimentalNoOffersCardEnabled = useIsExperimentalNoOffersCardEnabled();
  if (!isExperimentalNoOffersCardEnabled && (!hotel || !listOfValues)) {
    return <SkeletonLoader height={16} />;
  }
  const enabledCurrencies = getEnabledCurrencies(process.env.NEXT_PUBLIC_ENABLED_CURRENCIES);
  const currency = enabledCurrencies.find(currency => currency.code === currencyCode);
  const currencySymbol = prop('symbol', currency);
  const starRatingLabel = getAppliedStarsLabel(filters?.starRatings);
  const {
    matchedFilters,
    unmatchedFilters
  } = getMatchingFilters(filters, hotel, listOfValues, formatMessage, starRatingLabel, currencySymbol);
  if (!unmatchedFilters.length) {
    return null;
  }
  const WrapperComponent = isExperimentalNoOffersCardEnabled ? ScrollWrapperB : ScrollWrapper;
  return <>
      {isMobile && !isExperimentalNoOffersCardEnabled && <PartialFiltersDivider className={cx(hasBasicPropertyDetails && '__paddingTop')} />}
      <WrapperComponent>
        <Text colorPath="content.neutral.c500" variant="bodyXS" as="span">
          <FormattedMessageWrapper id="yourFilters" defaultMessage="Your filters" />
          :
        </Text>
        {matchedFilters.map(filter => <>
            <Text as="span" colorPath="content.neutral.c800" variant="labelXS" key={filter} className={paddingStyle}>
              {filter}
            </Text>
            <MiddleDot />
          </>)}
        {unmatchedFilters.map(filter => <>
            <Text className={cx(paddingStyle, strikeThrough)} colorPath="content.neutral.c500" variant="bodyXS" key={filter} as="span">
              {filter}
            </Text>
            <MiddleDot />
          </>)}
      </WrapperComponent>
    </>;
};
export const PartialFiltersMatch = React.memo(PartialFiltersMatchComponent);

require("./PartialFiltersMatch.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PartialFiltersMatch.tsx");